import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "mise-a-jour-windows-bloquee",
  silo: "windows",
  h1: "Mise à jour Windows bloquée ou en erreur : comment débloquer la situation ?",
  metaTitle: "Mise à jour Windows bloquée : comment résoudre le problème",
  metaDescription: "La mise à jour Windows tourne en rond ou échoue ? Ne paniquez pas. Voici les étapes pour débloquer la situation sans perdre vos données.",
  excerpt: "Votre PC est coincé à 'Préparation des mises à jour : 35%' depuis 2 heures ? Ou Windows Update affiche une erreur sans explication ? Voici comment débloquer la situation en toute sécurité.",
  publishedAt: "2024-01-18",
  readingMinutes: 6,
  relatedSlugs: ["ecran-bleu-bsod", "ordinateur-ne-demarre-plus", "erreurs-windows-frequentes", "wifi-ne-fonctionne-plus"],
  faq: [
    {
      question: "Combien de temps peut durer une mise à jour Windows ?",
      answer: "Une mise à jour mineure prend 15 à 30 minutes. Une mise à jour majeure (mise à niveau de version) peut prendre 1 à 3 heures selon la vitesse du PC. Au-delà de 3 heures sans progression visible, quelque chose ne va pas.",
    },
    {
      question: "Puis-je éteindre mon PC si la mise à jour est bloquée ?",
      answer: "C'est risqué d'éteindre un PC pendant une mise à jour -- vous pouvez corrompre Windows. Attendez au moins 2 heures, vérifiez que le disque dur est actif (voyant qui clignote) avant de décider d'éteindre.",
    },
    {
      question: "Pourquoi les mises à jour Windows échouent-elles ?",
      answer: "Les causes les plus fréquentes : espace disque insuffisant (moins de 10 Go libres), connexion internet coupée pendant le téléchargement, service Windows Update défaillant, ou fichiers système corrompus.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Les mises à jour Windows sont importantes pour la sécurité et la stabilité du système. Quand elles se bloquent, c'est frustrant -- et potentiellement problématique. Voici les solutions dans l'ordre, de la plus simple à la plus technique.",
    },
    {
      type: "h2",
      text: "Avant tout : vérifiez que la mise à jour progresse vraiment",
    },
    {
      type: "p",
      text: "Une mise à jour bloquée à un pourcentage peut en réalité être en train de travailler en arrière-plan. Vérifiez que le voyant du disque dur clignote. Si oui, attendez encore 30 à 60 minutes. Si le disque dur ne montre aucune activité depuis plus d'une heure, là vous avez un vrai blocage.",
    },
    {
      type: "h2",
      text: "Solution 1 : redémarrer le service Windows Update",
    },
    {
      type: "p",
      text: "Ouvrez l'invite de commandes en administrateur et tapez :\n• net stop wuauserv\n• net stop bits\n• net start wuauserv\n• net start bits\nCela redémarre le service Windows Update sans toucher aux mises à jour en cours.",
    },
    {
      type: "h2",
      text: "Solution 2 : utiliser l'outil de dépannage Windows Update",
    },
    {
      type: "p",
      text: "Windows propose un outil automatique : Paramètres > Système > Dépannage > Autres dépanneurs > Windows Update > Exécuter. Il détecte et corrige les problèmes courants automatiquement.",
    },
    {
      type: "h2",
      text: "Solution 3 : libérer de l'espace disque",
    },
    {
      type: "p",
      text: "Windows a besoin d'au moins 10 à 20 Go libres pour installer une mise à jour majeure. Si votre [disque est saturé](slug:disque-dur-sature), la mise à jour échouera systématiquement. Libérez de l'espace avant de relancer.",
    },
    {
      type: "h2",
      text: "Solution 4 : vider le cache Windows Update",
    },
    {
      type: "p",
      text: "Des fichiers de mise à jour corrompus peuvent bloquer l'installation. Pour vider ce cache :\n1. Arrêtez le service wuauserv (voir solution 1)\n2. Supprimez le contenu du dossier C:\\Windows\\SoftwareDistribution\\Download\n3. Redémarrez le service et relancez Windows Update",
    },
    {
      type: "callout",
      variant: "tip",
      title: "Assistant de mise à jour Windows",
      text: "Si Windows Update ne fonctionne pas, téléchargez l'outil 'Assistant de mise à jour Windows' directement depuis microsoft.com. Il contourne les problèmes de Windows Update et installe la dernière version de Windows proprement.",
    },
    {
      type: "h2",
      text: "Solution 5 : réparer les fichiers système",
    },
    {
      type: "p",
      text: "Des fichiers système corrompus peuvent empêcher les mises à jour. Ouvrez l'invite de commandes en admin et tapez 'sfc /scannow'. Attendez la fin de l'analyse (10-15 min) et redémarrez.",
    },
    {
      type: "h2",
      text: "Mise à jour qui bloque le démarrage de Windows",
    },
    {
      type: "p",
      text: "Si Windows reste bloqué au démarrage sur l'écran de mise à jour, consultez notre guide sur l'[ordinateur qui ne démarre plus](slug:ordinateur-ne-demarre-plus). Vous pouvez aussi accéder aux options de récupération (F8 ou 3 redémarrages forcés) pour désinstaller la mise à jour problématique. Si ce problème entraîne un [écran bleu](slug:ecran-bleu-bsod), il faut agir rapidement.",
    },
  ],
};

export default article;
